<template>
  <div class="futuri-post-web-widget-v2-player">
    <!-- 🔴 D-257 (A9): v2's loading, error and now-playing states were plain `<div>`s
         whose text changed with nobody told.
         🔑 MOUNTED HERE, not in `Playlist` and `NowPlayingDetail` where the row files
         them, and that placement IS the fix. Every datum the announcements need —
         `isLoading`, `errorMessage`, `episodeList`, `activeEpisodeId`,
         `isActiveEpisodePaused` — already lives in this component; announcing from the
         two leaves would have meant threading state DOWN to describe it and would have
         given v2 two regions where A3 built one shared component.
         🪤 And it is the shared `LiveRegion`, unconditional and empty, for that
         component's own reason: a region created at the moment it gains text is
         announced by nobody. -->
    <live-region :status="statusMessage" :failure="failureMessage"></live-region>
    <v2-player-layout
      :dark-theme="darkTheme"
      :compact-on-mobile="mods.compactOnMobile"
    >
      <template #top-half>
        <now-playing-section
          :show-image-src="showImageSrc"
          :dark-theme="darkTheme"
          :show-title="showTitle"
          :active-episode="episodeCurrentlyPlaying"
          :episode-images="mods.episodeImages"
          :compact-on-mobile="mods.compactOnMobile"
          :station="station"
          :drop-to-live="dropToLive"
          :use-m4a="useM4a"
          :api-prefix="apiPrefix"
          @audio-player:ready="onAudioPlayerReady"
          @audio:pause="onActiveEpisodePaused"
          @audio:play="onActiveEpisodePlaying"
          @image-loaded="onImageLoaded"
        >
        </now-playing-section>
      </template>
      <template #bottom-half>
        <playlist
          :dark-theme="darkTheme"
          :episode-list="episodeList"
          :error-message="errorMessage"
          :active-episode-id="activeEpisodeId"
          :is-loading="isLoading"
          :is-active-episode-paused="isActiveEpisodePaused"
          @episode-click="onPlaylistEpisodeClick"
          @episode-pause-button="onPlaylistEpisodePauseButton"
        ></playlist>
      </template>
    </v2-player-layout>
  </div>
</template>

<script>
import { reportNotAllowedIfApplicable } from '../core/faro';
import LiveRegion from '../shared-components/LiveRegion.vue';
import V2PlayerLayout from './V2PlayerLayout';
import NowPlayingSection from './NowPlayingSection';
import Playlist from './Playlist';
import { getJson } from '../utils/http';

export default {
  name: 'v2-player',
  // Declared so it is a real event, not an attribute falling through to the root
  // element — app.vue binds `@image-loaded` here.
  emits: ['image-loaded'],
  components: {
    LiveRegion,
    V2PlayerLayout,
    NowPlayingSection,
    Playlist,
  },
  props: {
    darkTheme: Boolean,
    modsList: String,
    station: String,
    apiResourceUrl: {
      type: String,
      required: true,
    },
    apiPrefix: {
      type: String,
      required: true,
    },
    dropToLive: Boolean,
    useM4a: Boolean,
  },
  data() {
    return {
      audioPlayer: null,
      showImageSrc: undefined,
      showTitle: undefined,
      isLoading: true,
      isActiveEpisodePaused: undefined, // TODO: rename to isActiveEpisodePlaying
      errorMessage: undefined,
      episodeList: [],
      activeEpisodeId: undefined,
      // D-257 (A9): the two announcement channels. Separate props on `LiveRegion`
      // because `role="alert"` interrupts and only failures may.
      statusMessage: '',
      failureMessage: '',
      // The episode whose title has already been announced, so resuming after a pause
      // says "Playing" rather than repeating the title. Seeded null so the first play
      // does announce it — v4's `announcedPodcastId`, same reasoning.
      announcedEpisodeId: null,
    };
  },
  computed: {
    episodeCurrentlyPlaying() {
      return this.activeEpisodeId
        ? this.getEpisodeFromListById(this.activeEpisodeId)
        : undefined;
    },

    mods() {
      return {
        episodeImages:
          !!this.modsList &&
          this.modsList.split(',').includes('episode_images'),
        compactOnMobile:
          !!this.modsList &&
          this.modsList.split(',').includes('compact_on_mobile'),
      };
    },
  },
  methods: {
    retrieveShowData() {
      // MR-49 (P5-9): hand-rolled XHR Promise -> shared getJson helper. getJson
      // reproduces this method's exact contract (200 -> parsed JSON; non-200 ->
      // Error(`${statusText} (${status})`)), so this is behaviour-preserving. The
      // v2 caller (onReady) catches into handleShowDataError -> errorMessage; the
      // valid-JSON + 500 v2-data-edge fixtures resolve/reject identically here.
      return getJson(this.apiResourceUrl);
    },

    processShowData({
      // eslint-disable-next-line camelcase
      show_image, show_rectangle_image, show_square_image, show_title, playlists,
    }) {
      this.isLoading = false;

      // PS-20 (MR-A): the POST widget.json emits the show cover as
      // `show_rectangle_image` / `show_square_image`; the legacy `show_image`
      // key is (effectively) never populated, so reading it alone left the
      // cover perpetually on the placeholder. Prefer the rectangle (the
      // now-playing image slot is 700×366), then the square, then the legacy key.
      // eslint-disable-next-line camelcase
      this.showImageSrc = show_rectangle_image || show_square_image || show_image;
      // eslint-disable-next-line
      this.showTitle = show_title;

      // An "episode" is known as a "playlist" on the server
      this.episodeList = playlists.map(
        ({
          id,
          name,
          description,
          image,
          m4a_file,
          timestamp_created,
          timestamp_updated,
          widget_publish_date,
        }) => ({
          id,
          name,
          description,
          image,
          m4a_file,
          // eslint-disable-next-line
          timestamp_created: new Date(timestamp_created * 1000),
          // eslint-disable-next-line
          timestamp_updated: new Date(timestamp_updated * 1000),
          // eslint-disable-next-line
          widget_publish_date: widget_publish_date ? new Date(widget_publish_date * 1000) : null,
        })
      );

      // Set active episode to the first episode
      // if (this.episodeList.length > 0) {
      //  this.setActiveEpisode(this.episodeList[0].id);
      // }}
    },

    handleShowDataError(error) {
      this.isLoading = false;
      this.errorMessage = error.message;
    },

    getEpisodeFromListById(episodeId) {
      return this.episodeList.reduce((acc, episode) => {
        if (episode.id === episodeId) {
          return episode;
        }
        return acc;
      }, {});
    },

    setActiveEpisode(episodeId) {
      this.isActiveEpisodePaused = true;
      this.activeEpisodeId = episodeId;
    },

    onPlaylistEpisodeClick(episodeId) {
      if (this.activeEpisodeId !== episodeId) {
        this.setActiveEpisode(episodeId);
      } else {
        // If episode that was clicked is already the active episode, play/resume it.
        Promise.resolve(this.audioPlayer.play())
          .then(() => {
            this.isActiveEpisodePaused = false;
          })
          .then(null, (error) => {
            // 🔴 D-203 — thenable-safe WITHOUT collapsing to `.then(f, h)`.
            //
            // The chained form was kept on purpose: in `.then(f).catch(h)` the handler also
            // catches a throw from `f`, and collapsing to `.then(f, h)` silently drops that.
            // But `.play()` may return a host-patched thenable with no `.catch` (D-176), so
            // the chain could not be left as it was.
            //
            // 🪤 `.play().then(f).then(null, h)` DOES NOT FIX IT. On a minimal thenable,
            // `T.then(f)` returns whatever that `then` returns — possibly `undefined` — and
            // `.then(null, h)` on undefined throws the same TypeError one link further down.
            // Wrapping FIRST is what makes the rest of the chain native and therefore safe.
            //
            // 🔑 AND ONCE WRAPPED, `.catch(h)` WOULD BE EQUALLY SAFE -- `.catch(h)` IS
            // DEFINED as `.then(undefined, h)`, so on the native promise the wrap returns
            // the two forms are identical. `.then(null, h)` is used here for consistency
            // with the rest of the tree, a convention pinned since D-176 by
            // test/unit/play-rejection-handler-shape.spec.js. Do NOT read this block as
            // "`.catch` is unsafe on a native promise" -- it is not, and an earlier
            // version of this comment implied exactly that.
            // 🔴 D-146 — v2's playlist play buttons report the NotAllowed measurement.
            // ⚠️ Found by the DERIVED gate, not by me: my hand-picked file list for this
            // fix was podcast.vue / LiveAudioPlayer.vue / v3 / v4, and missed this file
            // entirely. That is the ninth one-of-N in this work being caught by the very
            // gate written to stop the ninth one-of-N.
            reportNotAllowedIfApplicable(error, this.station);
            // eslint-disable-next-line
            console.error(error.message);
          });
      }
    },

    onPlaylistEpisodePauseButton() {
      this.isActiveEpisodePaused = true;
      this.audioPlayer.pause();
    },

    // D-233 (A9): `onPlaylistEpisodePlayButton` WAS HERE, and it is deleted rather than
    // left unbound. `Playlist`'s row conversion collapsed three controls into one, and
    // this handler had already been measured to be transition-for-transition identical to
    // `onPlaylistEpisodeClick` above — `setActiveEpisode` when the ids differ or none is
    // set, `play()` when they match. What went with it was a SECOND verbatim copy of the
    // D-203 thenable chain and the D-146 faro report, which is the kind of duplicate that
    // gets fixed in one place and not the other. A leg pins that the emit is gone too.

    // Final hop of the cover-loaded resize signal: app.vue listens for
    // `image-loaded` on <v2-player> and routes it into informResize, the same
    // contract v1 and (since !209) v4 use.
    onImageLoaded() {
      this.$emit('image-loaded');
    },

    onAudioPlayerReady(audioPlayer) {
      this.audioPlayer = audioPlayer;
    },

    onActiveEpisodePaused() {
      // Updated by LiveAudioPlayer
      this.isActiveEpisodePaused = true;
    },

    onActiveEpisodePlaying() {
      // Updated by LiveAudioPlayer
      this.isActiveEpisodePaused = false;
    },
    onReady() {
      if (!!this.apiResourceUrl) {
        this.retrieveShowData()
          .then(this.processShowData)
          .catch(this.handleShowDataError);
      }
    },

    // D-257 (A9): put a line through the polite channel.
    //
    // 🪤 The empty-string reset is load-bearing. AT announces the DIFF against what the
    // region last held, so pausing twice in a row would announce once and then go
    // silent. `$nextTick` rather than a timer, so the clear and the set land in two
    // separate DOM patches instead of being coalesced into no change at all. This is
    // A3's `announce` verbatim, and it is duplicated rather than shared because the
    // three players hold their status in three different places — the REGION is shared,
    // which is the part that was worth extracting.
    announce(message) {
      this.statusMessage = '';
      this.$nextTick(() => {
        this.statusMessage = message;
      });
    },
  },
  // D-257 (A9): announced from WATCHERS, not from the handlers that cause the change.
  // `isActiveEpisodePaused` is written from four places in this file (setActiveEpisode,
  // the two play() resolutions, onActiveEpisodePaused/Playing) and announcing at each
  // site is how one of them gets missed — A3's reasoning, and v2 has more writers than
  // v4 did.
  watch: {
    isLoading(loading) {
      if (loading) {
        this.announce('Loading episodes');
        return;
      }
      // The failure has its own channel; announcing a count of zero over the top of an
      // error would bury it.
      if (this.errorMessage) return;
      // D-275: pluralize so a single-episode station does not say "1 episodes".
      const n = this.episodeList.length;
      this.announce(`${n} episode${n === 1 ? '' : 's'}`);
    },

    errorMessage(message) {
      // 🔑 Cleared as well as set, or a later identical failure announces nothing
      // because the text has not changed.
      this.failureMessage = message || '';
    },

    isActiveEpisodePaused(paused) {
      // Seeded `undefined`; the first real write is what starts the announcements.
      if (paused === undefined) return;

      if (!paused) {
        const episode = this.episodeCurrentlyPlaying;
        if (episode && episode.id && episode.id !== this.announcedEpisodeId) {
          this.announcedEpisodeId = episode.id;
          this.announce(`Now playing: ${episode.name || 'episode'}`);
          return;
        }
        this.announce('Playing');
        return;
      }

      // 🔴 SELECTING AN EPISODE IS NOT PAUSING ONE, and this guard is why the region
      // does not say "Paused" the instant a user picks a row. `setActiveEpisode` writes
      // `isActiveEpisodePaused = true` on the way IN, so every selection arrives here
      // looking like a pause. Keyed on whether this episode was ever announced as
      // playing — which is order-independent, unlike a flag set from the
      // `activeEpisodeId` watcher in the same flush.
      if (this.announcedEpisodeId !== this.activeEpisodeId) return;

      this.announce('Paused');
    },
  },
  mounted() {
    this.onReady();
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/variables-v2';
.futuri-post-web-widget-v2-player {
}
</style>
